import { createApi } from '@reduxjs/toolkit/query/react';

import fetchBaseQuery from '../fetchBaseQuery';
import {
    transformCustomTranslationStatusesResponse,
    transformGetSuggestionsResponse,
    transformToggleCompleteItemResponse,
    transformUpdateTranslationReviewedResponse,
} from './transform';
import type { EditorGlossaryData } from './types/glossary';
import type { TranslationMemoryData } from './types/translationMemory';

export interface GenericResponse {
    err: number | string;
}

export type ErrorMessage = {
    error: {
        data: {
            error: {
                code: number;
                message: string;
            };
        };
    };
};

export interface IgnoreQATestRequest {
    projectId: string;
    translationId: number;
    testNumber: number;
}

export interface IgnoreMismatchErrorsRequest {
    projectId: string;
    translationId: number;
}

export interface UpdateTranslationRequest {
    translationId: number;
    translation: string;
    languageId: number;
    isReviewed: boolean;
    markItemAsCompleted: boolean;
}

export interface UpdateTranslationError {
    data: {
        error: { code: number; message: string };
        qa: { level: string; messages: string[] };
    };
    status: number;
}

export interface UpdateTranslationReviewedRequest {
    translationId: number;
    isReviewed: boolean;
}

export type UpdateTranslationReviewedResponse =
    | { err: '0' | 1 }
    | {
          err: 0;
          proofread: boolean;
          taskAutocompleteData: null | SetToggleCompleteItemResponse;
          akeyId: number;
      };

export interface SetAutoReviewPreferenceRequest {
    projectId: string;
    autoReview: boolean;
}

export interface SetTranslationVerifiedRequest {
    translationId: number;
}

export interface SetTranslationVerifiedResponse {
    unverified: 0 | 1;
}

export interface SetToggleCompleteItemRequest {
    completed: boolean;
    projectId: string;
    taskId: number;
    taskItemId: number;
    referenceLanguageId: number;
    keyId: number;
    languageId: number;
}

export interface SaveCorrectionsRequest {
    translationId: number;
    comment: string;
}

export type TaskItemStatus = 'completed' | 'in_progress' | 'created' | 'queued';

export interface SetToggleCompleteItemResponse {
    changed: boolean;
    closedLanguage: Record<string, never> | null;
    closedTask: Record<string, never> | null;
    status: TaskItemStatus;
    success: boolean;
    taskId: number;
    taskItemId: number;
}

export interface ProofreadingResponse {
    err: GenericResponse;
    keyData: Record<string, never>;
}
export interface ProofreadingRequest {
    translationId: number;
}
export type Action = 'add' | 'remove';

type CustomTranslationStatusesRequest = {
    projectId: string;
    translationId: number;
    branchName?: string;
    action: Action;
    statusId: number;
};

export type CustomTranslationStatusesResponse = {
    translationId: string;
    ids: number[];
    taskAutoCompleteData: SetToggleCompleteItemResponse | null;
};

type GetTranslationSuggestionsRequest = {
    translationId: number;
    referenceLanguageId: number;
    languageId: number;
};

type MachineTranslationItem = {
    rtl: '' | ' direction: rtl; ';
    logo: 'logo_googletranslate.png' | 'logo_microsoft.svg' | 'logo_deepl.png';
    engine: 'google' | 'microsoft' | 'deepl';
};

export type GetTranslationSuggestionsResponse = {
    err: '0';
    task: {
        id: number;
        sourceLanguageId: number;
    } | null;
    base?: string;
    inlineData?: {
        glossary?: EditorGlossaryData;
        mt?: MachineTranslationItem[];
        tm?: TranslationMemoryData;
    };
};

export const getCustomStatusesUrlWithParams = (projectId: string, branchName?: string) => {
    const url = new URL(
        `/project/${projectId}/custom-translation-statuses/set-for-translation`,
        window.location.origin,
    );
    if (branchName) {
        url.searchParams.set('branch', branchName);
    }

    return url.toString();
};

const editorApi = createApi({
    reducerPath: 'lokaliseInternal_editor',
    baseQuery: fetchBaseQuery(),
    endpoints: (builder) => ({
        ignoreQATest: builder.mutation<GenericResponse, IgnoreQATestRequest>({
            query: ({ translationId, projectId, testNumber }) => {
                const formData = new FormData();
                formData.append('translationId', JSON.stringify(translationId));
                formData.append('testNumber', JSON.stringify(testNumber));

                return {
                    url: `/project/${projectId}/ignore-qa-test`,
                    method: 'POST',
                    body: formData,
                    headers: {
                        'x-auto-content-type': '1',
                    },
                };
            },
        }),
        ignoreMismatchErrors: builder.mutation<GenericResponse, IgnoreMismatchErrorsRequest>({
            query: ({ translationId, projectId }) => {
                const formData = new FormData();
                formData.append('trans_id', JSON.stringify(translationId));

                return {
                    url: `/project/${projectId}/ignore-p-mismatch`,
                    method: 'POST',
                    body: formData,
                    headers: {
                        'x-auto-content-type': '1',
                    },
                };
            },
        }),
        saveTranslation: builder.mutation<GenericResponse, UpdateTranslationRequest>({
            query: ({ translationId, translation, languageId, isReviewed, markItemAsCompleted }) => {
                const formData = new FormData();
                formData.append('translation', translation);
                formData.append('ref_lang', JSON.stringify(languageId));
                formData.append('action', isReviewed ? 'review' : 'confirm');
                formData.append('markItemAsCompleted', JSON.stringify(Number(markItemAsCompleted)));

                return {
                    url: `/translation/${translationId}/update`,
                    method: 'POST',
                    body: formData,
                    headers: {
                        'x-auto-content-type': '1',
                    },
                };
            },
        }),
        checkProofreading: builder.mutation<ProofreadingResponse, ProofreadingRequest>({
            query: ({ translationId }) => {
                const formData = new FormData();
                formData.append('translationId', 'translationId');

                return {
                    url: `/translation/${translationId}/check-proofreading`,
                    method: 'POST',
                };
            },
        }),
        saveCustomTranslationStatus: builder.mutation<
            CustomTranslationStatusesResponse,
            CustomTranslationStatusesRequest
        >({
            query: ({ projectId, branchName, translationId, action, statusId }) => ({
                url: getCustomStatusesUrlWithParams(projectId, branchName),
                method: 'PUT',
                body: {
                    action,
                    translationId,
                    statusId,
                },
                headers: {
                    'x-auto-content-type': '1',
                },
            }),
            transformResponse: transformCustomTranslationStatusesResponse,
        }),
        setToggleCompleteItem: builder.mutation<SetToggleCompleteItemResponse, SetToggleCompleteItemRequest>({
            query: ({ completed, projectId, taskId, taskItemId, referenceLanguageId, keyId, languageId }) => {
                const formData = new FormData();
                formData.append('completed', completed ? 'true' : 'false');
                formData.append('project_id', projectId);
                formData.append('task_id', JSON.stringify(taskId));
                formData.append('task_item_id', JSON.stringify(taskItemId));
                formData.append('reference_lang_id', JSON.stringify(referenceLanguageId));
                formData.append('akey_id', JSON.stringify(keyId));
                formData.append('lang_id', JSON.stringify(languageId));

                return {
                    url: '/tasks/toggle-complete-item',
                    method: 'POST',
                    body: formData,
                    headers: {
                        'x-auto-content-type': '1',
                    },
                };
            },
            transformResponse: transformToggleCompleteItemResponse,
        }),
        setTranslationReviewed: builder.mutation<UpdateTranslationReviewedResponse, UpdateTranslationReviewedRequest>({
            query: ({ translationId, isReviewed }) => {
                const formData = new FormData();
                formData.append('action', 'toggle_proofread');
                formData.append('t', `${translationId}`);
                formData.append('ref_lang_id', '');
                formData.append('state', isReviewed ? '1' : '0');

                return {
                    url: `/ajax`,
                    method: 'POST',
                    body: formData,
                    headers: {
                        'x-auto-content-type': '1',
                    },
                };
            },
            transformResponse: transformUpdateTranslationReviewedResponse,
        }),
        setTranslationVerified: builder.mutation<SetTranslationVerifiedResponse, SetTranslationVerifiedRequest>({
            query: ({ translationId }) => ({
                url: `/translation/${translationId}/toggle-unverified`,
                method: 'POST',
                headers: {
                    'x-auto-content-type': '1',
                },
            }),
        }),
        setAutoReviewPreference: builder.mutation<GenericResponse, SetAutoReviewPreferenceRequest>({
            query: ({ projectId, autoReview }) => {
                const formData = new FormData();
                formData.append('reviewed', autoReview ? '1' : '0');

                return {
                    url: `/project/${projectId}/toggle-user-reviewed-preference`,
                    method: 'POST',
                    body: formData,
                    headers: {
                        'x-auto-content-type': '1',
                    },
                };
            },
        }),
        saveCorrections: builder.mutation<GenericResponse, SaveCorrectionsRequest>({
            query: ({ translationId, comment }) => {
                const formData = new FormData();
                formData.append('trans_id', `${translationId}`);
                formData.append('comment', comment);
                formData.append('action', 'gengo_req_corr');

                return {
                    url: `/ajax`,
                    method: 'POST',
                    body: formData,
                    headers: {
                        'x-auto-content-type': '1',
                    },
                };
            },
        }),
        getTranslationSuggestions: builder.mutation<
            GetTranslationSuggestionsResponse,
            GetTranslationSuggestionsRequest
        >({
            query: ({ languageId, translationId, referenceLanguageId }) => {
                const formData = new FormData();
                formData.append('action', 'robot');
                formData.append('to_lang', `${languageId}`);
                formData.append('t', `${translationId}`);

                if (referenceLanguageId) {
                    formData.append('ref_lang', `${referenceLanguageId}`);
                }

                return {
                    url: '/ajax',
                    method: 'POST',
                    body: formData,
                    headers: {
                        'x-auto-content-type': '1',
                    },
                };
            },
            transformResponse: transformGetSuggestionsResponse,
        }),
    }),
});

export type {
    EditorGlossaryData,
    EditorGlossaryItem,
    EditorGlossaryLimitReachedItem,
    LanguageData,
} from './types/glossary';

export type {
    EditorTranslationMemoryItem,
    EditorTranslationMemoryLimitReachedItem,
    EditorTranslationMemoryMinimalItem,
    TranslationMemoryData,
} from './types/translationMemory';

export default editorApi;

export const {
    useIgnoreQATestMutation,
    useIgnoreMismatchErrorsMutation,
    useSaveTranslationMutation,
    useCheckProofreadingMutation,
    useSetTranslationReviewedMutation,
    useSetAutoReviewPreferenceMutation,
    useSetToggleCompleteItemMutation,
    useSetTranslationVerifiedMutation,
    useSaveCustomTranslationStatusMutation,
    useSaveCorrectionsMutation,
    useGetTranslationSuggestionsMutation,
} = editorApi;
